import { useStringParam } from '@/hooks/useParam';
import { cn } from '@/utils/shadcn';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';

interface SearchInputProps {
  placeholder: string;
}

export function SearchInput({ placeholder }: SearchInputProps) {
  const [searchQuery, setSearchQuery] = useStringParam({
    key: 'q',
    defaultValue: '',
    hideDefault: true,
  });
  const [value, setValue] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // If the searchQuery changes, update the input value (this can happen when using the omniSearch popover from the header while on the search page)
  useEffect(() => {
    setValue(searchQuery);
  }, [searchQuery]);

  return (
    <SearchInputPresentation
      ref={inputRef}
      value={value}
      placeholder={placeholder}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      onKeyUp={(e) => {
        // if enter is pressed, call onChange
        if (e.key === 'Enter') {
          if (searchQuery !== value) {
            setSearchQuery(value);
          }
        }
      }}
      onBlur={() => {
        if (searchQuery !== value) {
          setSearchQuery(value);
        }
      }}
    />
  );
}

export const SearchInputPresentation = forwardRef<
  HTMLInputElement,
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
>((props, ref) => {
  return (
    <div className="g-relative">
      <MdSearch className="g-absolute g-start-3 g-top-1/2 g--translate-y-1/2 g-text-gray-400 g-h-5 g-w-5" />
      <input
        type="text"
        ref={ref}
        {...props}
        className={cn(
          'g-w-full g-ps-10 g-pe-4 g-py-3 g-rounded-lg g-border g-border-solid g-border-gray-200 focus:g-border-primary-500 focus:g-ring-2 focus:g-ring-primary-200 g-transition-all g-outline-none g-text-lg',
          props?.className
        )}
      />
    </div>
  );
});
